import React from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  ScrollView,
} from "react-native";
import { useTranslation } from 'react-i18next';
import CloseIcon from "../../../../../../../assets/images/CloseIcon.svg";
import Colors from "../../../../constants/colors";
import VintageClock from "../../../../../../../assets/images/Vintage-Clock.svg";
import CashRegister from "../../../../../../../assets/images/Cash-Register.svg";
import DiscountIcon from "../../../../../../../assets/images/Discount.svg";
import LeftAlign from "../../../../../../../assets/images/FeesIcon.svg";
import PeopleAddIcon from "../../../../../../../assets/images/People-Add.svg";
import LocationMap from "../../../../../../../assets/images/Location-Map.svg";
import AnimatedSuccessIcon from "../AnimatedSuccessView";
import { Flag } from "../../../Flag";
import { formatBillerDisplayName } from "../../../../helper/billerDisplayName";
import GradientDivider from "../../../uiComponents/GradientDevider";


interface TransferSummaryModalProps {
  visible: boolean;
  onClose: () => void;
  receiverName: string;
  amount: string; // e.g. "9,365.58 MXN"
  currency: string;
  status?: string; // e.g. "Processing"
  paymentMethod?: string; // e.g. "Bank account •••• 1188"
  exchangeRate?: string; // e.g. "1 USD = 18.73 MXN"
  fees?: string; // e.g. "0.00"
  youSent?: string; // e.g. "$500.00 USD"
  total?: string; // e.g. "500.00 USD"
  receiveMethod?: string; // e.g. "Debit card •••• 1188"
  isBillPay?: boolean;
}

const TransferSummaryComponent: React.FC<TransferSummaryModalProps> = ({
  visible,
  onClose,
  receiverName,
  amount,
  status,
  paymentMethod,
  exchangeRate,
  fees,
  youSent,
  total,
  receiveMethod,
  isBillPay,
  currency
}) => {
  const { t } = useTranslation();
  const displayStatus = status ?? t("STATUS_PROCESSING_VALUE");
  const displayReceiverName =
    isBillPay && receiverName ? formatBillerDisplayName(receiverName) : receiverName;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      presentationStyle="pageSheet"
      onRequestClose={onClose}
    >
      <View style={styles.container}>
        {/* Close button */}
        <TouchableOpacity style={styles.closeBtn} onPress={onClose} testID="closeTransferSummary">
          <CloseIcon width={24} height={24}/>
        </TouchableOpacity>

        <ScrollView contentContainerStyle={styles.scroll}>
          <View style={{ alignItems: 'center', marginBottom: 24}}>
            <AnimatedSuccessIcon />
            {/* Receiver + Amount */}
            <Text style={styles.receiverLine}>{displayReceiverName} {t("WILL_RECEIVE")}</Text>
            <Text style={styles.topAmount}>{amount}<Text style={styles.topCurrencyText}>{currency}</Text></Text>
          </View>
          <GradientDivider/>

          {/* Transfer Details */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>{t("TRANSFER_DETAILS")}</Text>
            <View style={styles.sectionBlock}>
              <VintageClock  width={24} height={24}/>
              <View style={styles.sectionBlockContent}>
                <Text style={styles.label}>{t("SEND_STATUS_PROCESSING")}</Text>
                <Text style={styles.value}>{displayStatus}</Text>
              </View>
            </View>
             <View style={styles.sectionBlock}>
              <CashRegister  width={24} height={24}/>
              <View style={styles.sectionBlockContent}>
                <Text style={styles.label}>{t("PAYMENT")}</Text>
                <Text style={styles.value}>{paymentMethod}</Text>
              </View>
            </View>

            <View style={styles.sectionBlock}>
              <DiscountIcon  width={24} height={24}/>
              <View style={styles.sectionBlockContent}>
                <Text style={styles.label}>{t("EXHANGE_RATE")}</Text>
                <Text style={styles.value}>{exchangeRate}</Text>
              </View>
            </View>

            <View style={styles.sectionBlock}>
              <LeftAlign  width={24} height={24}/>
              <View style={styles.sectionBlockContent}>
                <Text style={styles.label}>{t("FEES")}</Text>
                <Text style={styles.value}>{fees}</Text>
              </View>
            </View>

            <View style={styles.sectionBlock}>
              <Flag iso3="USA" width={24} height={24} />
              <View style={styles.sectionBlockContent}>
                <Text style={styles.label}>{t('YOU_SENT')}</Text>
                <Text style={styles.amountText}>{youSent}<Text style={[styles.currencyText, { marginLeft: 4}]}>{currency}</Text></Text>
              </View>
            </View>

            <View style={styles.sectionBlock}>
              {/* <Image source={EnglishFlag} style={styles.leftIcons} /> */}
              <View style={styles.sectionBlockContentIndented}>
                <Text style={styles.label}>{t("TOTAL_INCLUDES_FESS")}</Text>
                <Text style={styles.totalAmountText}>{total}<Text style={[styles.totalCurrencyText, { marginLeft: 4}]}>{currency}</Text></Text>
              </View>
            </View>
          </View>

          {/* Receiver Details */}
          <View style={[styles.section, { borderBottomWidth: 0}]}>
            <Text style={styles.sectionTitle}>{t("RECEIVER_DETAILS")}</Text>
            <View style={styles.sectionBlock}>
              <PeopleAddIcon  width={24} height={24}/>
              <View style={styles.sectionBlockContent}>
                <Text style={styles.label}>{t("LEGAL_NAME")}</Text>
                <Text style={styles.value}>{displayReceiverName}</Text>
              </View>
            </View>
            {!isBillPay && (
              <View style={styles.sectionBlock}>
                <LocationMap  width={24} height={24}/>
                <View style={styles.sectionBlockContent}>
                  <Text style={styles.label}>{t("RECEIVE_METHOD_TITLE")}</Text>
                  <Text style={styles.value}>{receiveMethod}</Text>
                </View>
              </View>
            )}
          </View>
        </ScrollView>
      </View>
    </Modal>
  );
};

export default TransferSummaryComponent;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
  },
  closeBtn: {
    alignSelf: "flex-end",
    padding: 24,
  },
  scroll: {
    paddingHorizontal: 24,
    paddingBottom: 40,
    // alignItems: "center",
  },
  circleAvatar: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: "#eee",
    marginBottom: 24,
  },
  successIconStyle: {
    width: 80,
    height: 80,
    borderRadius: 40,
    backgroundColor: "#eee",
    marginBottom: 24,
  },
  receiverLine: {
    fontFamily: 'Rubik',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: 22,
    color: Colors.titleTextColor,
    marginBottom: 6,
    marginTop: 24,
    textAlign: "center",
    width: "100%",
  },
  amount: {
    fontSize: 24,
    fontWeight: "500",
    marginBottom: 24,
    fontFamily: 'Rubik',
    lineHeight: 30,
    color: Colors.titleTextColor,
    textAlign: "center",
    width: "100%",
  },
  topAmount: {
    fontSize: 40,
    fontWeight: "500",
    fontFamily: 'Rubik',
    lineHeight: 46,
    color: Colors.titleTextColor,
  },
  topCurrencyText: {
    fontSize: 24,
    fontWeight: "500",
    fontFamily: 'Rubik',
    lineHeight: 30,
    color: Colors.subTitleColor,
    paddingLeft: 8
  },
  section: {
    width: "100%",
    // borderTopWidth: 1,
    borderBottomWidth: 1,
    borderBottomColor: "#ddd",
    paddingTop: 36,
    paddingBottom: 24
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: "500",
    marginBottom: 12,
    fontFamily: 'Rubik',
    lineHeight: 26,
    color: Colors.titleTextColor,
  },
  sectionBlock: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 12,
  },
  sectionBlockContent: {
    marginLeft: 20,
    flex: 1,
    minWidth: 0,
  },
  sectionBlockContentIndented: {
    marginLeft: 46,
    flex: 1,
    minWidth: 0,
  },
  leftIcons: {
    width: 24,
    height: 24,
    marginRight: 20
  },
  label: {
    fontSize: 16,
    fontWeight: "500",
    marginBottom: 4,
    fontFamily: 'Rubik',
    lineHeight: 24,
    color: Colors.titleTextColor,
  },
  value: {
    fontSize: 14,
    fontWeight: "400",
    fontFamily: 'Rubik',
    lineHeight: 20,
    color: Colors.infoSubValue,
    flexShrink: 1,
  },
  flagWrapper: {
    width: 24,
    height: 24,
    marginRight: 20,
  },
  amountText: {
    fontSize: 16,
    fontWeight: "500",
    fontFamily: 'Rubik',
    lineHeight: 16,
    color: Colors.titleTextColor,
  },
  currencyText: {
    fontSize: 16,
    fontWeight: "500",
    fontFamily: 'Rubik',
    lineHeight: 16,
    color: Colors.subTitleColor,
  },
  totalAmountText: {
    fontSize: 24,
    fontWeight: "500",
    fontFamily: 'Rubik',
    lineHeight: 16,
    color: Colors.titleTextColor,
  },
  totalCurrencyText: {
    fontSize: 16,
    fontWeight: "500",
    fontFamily: 'Rubik',
    lineHeight: 16,
    color: Colors.titleTextColor,
  },
});